/**
 * Right-of-way labels of the transport view ("Lộ giới ≈ 21,5 m" along today's roads).
 * The official system publishes no red-line layer: the width is measured between the facing
 * land-use cells of the zone / detail plans (core/roadplan.js rightOfWayRuns) along today's road
 * centrelines (OpenStreetMap). Computed per z15 tile from the plan cells and the OpenFreeMap z14
 * road tile in the compute worker (compute/impl.js rowRuns); shown from zoom 16.
 */
import { bus } from './state.js';
import { tilesForBBox } from './core/geo.js';
import { compute } from './compute.js';
import { labelsOn } from './labels.js';
import { GT } from './transport.js';

const MIN_ZOOM = 15.5;
const tiles = new Map(); // "x/y" (z15) -> runs[]
const loading = new Set();
let pm = null, timer = 0;
const nf = new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 1 });

const active = () => pm && pm.ov.plan === GT && labelsOn() && pm.map.getZoom() >= MIN_ZOOM;

export function initRowLabels(planMap) {
  pm = planMap;
  const m = pm.map;
  m.on('moveend', () => { clearTimeout(timer); timer = setTimeout(refresh, 160); });
  m.on('style.load', () => { render(); refresh(); });
  bus.on('plan', () => { render(); refresh(); });
  bus.on('labels', () => { render(); refresh(); });
  timer = setTimeout(refresh, 0);
}

function render() {
  if (!pm) return;
  if (!active()) { pm.setData('row', null); return; }
  const features = [];
  for (const list of tiles.values()) {
    for (const r of list) {
      features.push({
        type: 'Feature',
        properties: { t: `Lộ giới ≈ ${nf.format(r.w >= 25 ? Math.round(r.w) : Math.round(r.w * 2) / 2)} m`, rot: r.rot, len: r.len, w: r.w },
        geometry: { type: 'Point', coordinates: r.p },
      });
    }
  }
  pm.setData('row', { type: 'FeatureCollection', features });
}

const loadTile = (x, y) => compute('rowRuns', [x, y]);

function refresh() {
  if (!active()) { render(); return; }
  const b = pm.map.getBounds();
  const want = tilesForBBox([b.getWest(), b.getSouth(), b.getEast(), b.getNorth()], 15).slice(0, 12);
  for (const t of want) {
    const k = `${t.x}/${t.y}`;
    if (tiles.has(k) || loading.has(k)) continue;
    loading.add(k);
    loadTile(t.x, t.y).then((runs) => {
      tiles.set(k, runs);
      if (tiles.size > 60) tiles.delete(tiles.keys().next().value);
      requestAnimationFrame(render);
    }).catch(() => {}).finally(() => loading.delete(k));
  }
  render();
}
